

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar, CheckCircle, Mail, Bell } from 'lucide-react';
import { toast } from 'sonner';

export default function UserDashboard() {
  const [stats, setStats] = useState(null);
  const { axiosInstance, user } = useAuth();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Échec du chargement des statistiques');
    }
  };

  const statCards = stats ? [
    {
      title: 'Mes horaires',
      value: stats.my_schedules,
      icon: Calendar,
      color: 'bg-blue-500',
      testId: 'stat-my-schedules'
    },
    {
      title: 'Services complétés',
      value: stats.completed_schedules,
      icon: CheckCircle,
      color: 'bg-green-500',
      testId: 'stat-completed'
    },
    {
      title: 'Messages non lus',
      value: stats.unread_messages,
      icon: Mail,
      color: 'bg-purple-500',
      testId: 'stat-unread-messages'
    },
    {
      title: 'Notifications',
      value: stats.unread_notifications,
      icon: Bell,
      color: 'bg-orange-500',
      testId: 'stat-notifications'
    },
  ] : [];

  if (!user) return null;

  return (
    <Layout title={`Bienvenue, ${user?.first_name || user?.name || 'Utilisateur'}!`}>
      <div className="space-y-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="glass-card border-amber-200" data-testid={stat.testId}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">{stat.title}</CardTitle>
                  <div className={`${stat.color} p-2 rounded-lg`}>
                    <Icon className="text-white" size={20} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-amber-900" data-testid={`${stat.testId}-value`}>
                    {stat.value}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <Card className="glass-card border-amber-200">
          <CardHeader>
            <CardTitle className="text-amber-900">Actions rapides</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              
              <Link
                to="/schedules"
                className="p-4 bg-gradient-to-br from-amber-100 to-yellow-100 rounded-xl hover:shadow-lg transition cursor-pointer"
                data-testid="quick-action-schedules"
              >
                <Calendar className="text-amber-700 mb-2" size={32} />
                <h3 className="font-semibold text-amber-900">Mes horaires</h3>
                <p className="text-sm text-gray-600">Gérer mes disponibilités</p>
              </Link>

              <Link
                to="/messages"
                className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl hover:shadow-lg transition cursor-pointer"
                data-testid="quick-action-messages"
              >
                <Mail className="text-purple-700 mb-2" size={32} />
                <h3 className="font-semibold text-purple-900">Messages</h3>
                <p className="text-sm text-gray-600">Messagerie avec collègues</p>
              </Link>

              <Link
                to="/mes-prestations"
                className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl hover:shadow-lg transition cursor-pointer"
                data-testid="quick-action-prestations"
              >
                <Calendar className="text-blue-700 mb-2" size={32} />
                <h3 className="font-semibold text-blue-900">Mes prestations</h3>
                <p className="text-sm text-gray-600">Voir et valider mes prestations assignées</p>
              </Link>

              <Link
                to="/exchanges"
                className="p-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl hover:shadow-lg transition cursor-pointer"
                data-testid="quick-action-exchanges"
              >
                <CheckCircle className="text-green-700 mb-2" size={32} />
                <h3 className="font-semibold text-green-900">Échanges</h3>
                <p className="text-sm text-gray-600">Échanger des prestations</p>
              </Link>

            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
